import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router';
import { doc, collection, addDoc, setDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType, stripUndefinedForFirestore } from '../lib/firebase';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { LexGatWordmark } from '../components/Logo';
import TermsContent from '../components/TermsContent';
import PhoneInput from '../components/PhoneInput';
import CityAutocompleteInput from '../components/CityAutocompleteInput';
import LawCollegeAutocompleteInput from '../components/LawCollegeAutocompleteInput';
import RoleSelectCards from '../components/RoleSelectCards';
import OnboardingShell from '../components/onboarding/OnboardingShell';
import OnboardingStepWheel from '../components/onboarding/OnboardingStepWheel';
import OnboardingWelcomeMoment from '../components/onboarding/OnboardingWelcomeMoment';
import { onboardingClasses } from '../lib/onboardingTheme';
import { cn } from '../lib/utils';
import {
  TERMS_VERSION,
  type ProfileData,
  type AccountType,
  type OnboardingQuestion,
  type OnboardingPhase,
  validateStep,
  validateSignupProfile,
  validateDeferredProfile,
  normalizeProfileData,
  isStepCompleted,
  getNextStepIndex,
  getPrevStepIndex,
  getOnboardingQuestions,
  getDeferredQuestions,
  getFirstIncompleteDeferredStepIndex,
  accountTypeToWorkspaceType,
  applyAccountTypeChange,
  formatProfileTextFieldBlur,
  formatProfileTextFieldLive,
  isTypedProfileTextField,
  normalizeAccountType,
} from '../lib/onboardingProfile';
import { getAuthProfilePrefill } from '../lib/authProfilePrefill';
import { useOnboardingScrollNav } from '../hooks/useOnboardingScrollNav';
import { useOnboardingStepFocus } from '../hooks/useOnboardingStepFocus';
import type { TypedProfileTextField } from '../lib/textFormat';

const DEFAULT_PHONE_COUNTRY = 'PK';

interface OnboardingProps {
  phase?: OnboardingPhase;
}

export default function Onboarding({ phase: phaseProp }: OnboardingProps = {}) {
  const { user, userData, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const phase: OnboardingPhase =
    phaseProp ?? (location.pathname === '/complete-profile' ? 'complete' : 'signup');
  const isSignup = phase === 'signup';
  const { showError, showSuccess } = useToast();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<ProfileData>({ phoneCountry: DEFAULT_PHONE_COUNTRY });
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showWelcomeMoment, setShowWelcomeMoment] = useState(false);

  const animatingRef = useRef(false);
  const authPrefillApplied = useRef(false);
  const profileHydrated = useRef(false);
  const onboardingRootRef = useRef<HTMLDivElement>(null);

  const questions = useMemo(
    () => getOnboardingQuestions(formData.accountType, phase),
    [formData.accountType, phase],
  );

  useEffect(() => {
    if (isSignup) {
      if (userData?.onboarded && userData?.currentWorkspaceId && !showWelcomeMoment) {
        navigate('/dashboard');
      }
      return;
    }
    if (!userData?.onboarded) {
      navigate('/onboarding');
      return;
    }
    if (userData.profileComplete) {
      navigate('/dashboard');
    }
  }, [userData, navigate, isSignup, showWelcomeMoment]);

  useEffect(() => {
    if (isSignup || profileHydrated.current) return;
    if (!userData?.profileData) return;
    profileHydrated.current = true;
    const saved = userData.profileData as ProfileData;
    setFormData((prev) => ({
      phoneCountry: DEFAULT_PHONE_COUNTRY,
      ...saved,
      ...prev,
      accountType: saved.accountType ?? prev.accountType,
    }));
    const deferredQs = getDeferredQuestions(saved.accountType);
    setCurrentStep(getFirstIncompleteDeferredStepIndex(saved, deferredQs));
  }, [isSignup, userData]);

  useEffect(() => {
    if (currentStep >= questions.length) {
      setCurrentStep(Math.max(0, questions.length - 1));
    }
  }, [questions.length, currentStep]);

  useEffect(() => {
    if (!isSignup || !user || authPrefillApplied.current) return;
    const hints = getAuthProfilePrefill(user);
    if (!hints.fullName && !hints.phone) return;

    authPrefillApplied.current = true;
    setFormData((prev) => ({
      ...prev,
      phoneCountry: prev.phoneCountry || hints.phoneCountry || DEFAULT_PHONE_COUNTRY,
      ...(prev.fullName || !hints.fullName ? {} : { fullName: hints.fullName }),
    }));
  }, [user, isSignup]);

  const runStepValidation = useCallback(
    (stepIndex: number, data: ProfileData = formData): boolean => {
      const { valid, errors } = validateStep(stepIndex, data, questions);
      setStepErrors(errors);
      return valid;
    },
    [formData, questions],
  );

  const advanceToStep = (nextStep: number, data: ProfileData = formData) => {
    if (nextStep < questions.length) {
      animatingRef.current = true;
      setCurrentStep(nextStep);
      setTimeout(() => (animatingRef.current = false), 600);
    } else if (isSignup) {
      const signup = validateSignupProfile(data);
      if (!signup.valid) {
        setStepErrors(signup.errors);
        showError('Please complete all required fields before continuing.');
        return;
      }
      setShowTerms(true);
    } else {
      void submitCompleteProfile(data);
    }
  };

  const handleNext = () => {
    if (animatingRef.current) return;
    if (!runStepValidation(currentStep)) return;

    const nextStep = getNextStepIndex(currentStep, formData, questions);
    setStepErrors({});
    advanceToStep(nextStep);
  };

  const handlePrev = () => {
    if (animatingRef.current || currentStep === 0) return;
    const prevStep = getPrevStepIndex(currentStep);
    setStepErrors({});
    animatingRef.current = true;
    setCurrentStep(prevStep);
    setTimeout(() => (animatingRef.current = false), 600);
  };

  const goToStep = (stepIndex: number) => {
    if (animatingRef.current || stepIndex === currentStep) return;
    if (stepIndex < 0 || stepIndex >= questions.length) return;
    setStepErrors({});
    animatingRef.current = true;
    setCurrentStep(stepIndex);
    setTimeout(() => (animatingRef.current = false), 600);
  };

  useOnboardingScrollNav({
    rootRef: onboardingRootRef,
    currentStep,
    stepCount: questions.length,
    onGoToStep: goToStep,
    enabled: questions.length > 0,
  });

  useOnboardingStepFocus(onboardingRootRef, currentStep, questions);

  const handleAccountTypeSelect = (accountType: AccountType) => {
    const nextData = applyAccountTypeChange(formData, accountType);
    setFormData(nextData);
    setStepErrors((prev) => {
      const next = { ...prev };
      delete next.accountType;
      return next;
    });

    if (currentStep !== 0) return;

    setTimeout(() => {
      if (animatingRef.current) return;
      setStepErrors({});
      animatingRef.current = true;
      setCurrentStep(1);
      setTimeout(() => (animatingRef.current = false), 600);
    }, 300);
  };

  const handleSelectOption = (questionId: string, option: string, isMulti: boolean) => {
    if (isMulti) {
      const current = (formData[questionId as keyof ProfileData] as string[]) || [];
      const updated = current.includes(option)
        ? current.filter((item: string) => item !== option)
        : [...current, option];
      setFormData({ ...formData, [questionId]: updated });
      setStepErrors((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    } else {
      const nextData = { ...formData, [questionId]: option };
      setFormData(nextData);
      setStepErrors((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      setTimeout(() => {
        const idx = questions.findIndex((q) => q.id === questionId);
        if (idx !== currentStep || animatingRef.current) return;
        if (!validateStep(currentStep, nextData, questions).valid) return;
        setStepErrors({});
        advanceToStep(getNextStepIndex(currentStep, nextData, questions), nextData);
      }, 300);
    }
  };

  const handleTextChange = (field: keyof ProfileData, value: string) => {
    const nextValue = isTypedProfileTextField(field as string)
      ? formatProfileTextFieldLive(field as TypedProfileTextField, value)
      : value;
    setFormData({ ...formData, [field]: nextValue });
    setStepErrors((prev) => {
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };

  const handleTextBlur = (field: keyof ProfileData, value: string) => {
    if (!isTypedProfileTextField(field as string)) return;
    const formatted = formatProfileTextFieldBlur(
      field as TypedProfileTextField,
      value,
    );
    if (formatted !== value) {
      setFormData((prev) => ({ ...prev, [field]: formatted }));
    }
  };

  const handlePhoneChange = (phone: string, phoneCountry: string) => {
    setFormData({ ...formData, phone, phoneCountry });
    setStepErrors((prev) => {
      const next = { ...prev };
      delete next.phone;
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNext();
    }
  };

  const submitSignup = async () => {
    if (!user) return;

    if (!user.email) {
      showError('An email address is required to complete setup. Please sign in with an account that has an email.');
      return;
    }

    const signup = validateSignupProfile(formData);
    if (!signup.valid) {
      setStepErrors(signup.errors);
      showError('Please fix the highlighted fields before submitting.');
      return;
    }

    const normalized = normalizeProfileData(formData);
    setIsSubmitting(true);

    try {
      const accountType = normalizeAccountType(normalized.accountType);
      const workspaceType = accountTypeToWorkspaceType(accountType);
      const firmName =
        normalized.firmName ||
        `${normalized.fullName || user.displayName || 'My'} Law Firm`;

      const workspaceRef = await addDoc(collection(db, 'workspaces'), {
        firmName,
        workspaceType,
        ownerId: user.uid,
        createdAt: new Date().toISOString(),
      });

      await setDoc(doc(db, 'workspaces', workspaceRef.id, 'members', user.uid), {
        workspaceId: workspaceRef.id,
        userId: user.uid,
        role: 'owner',
        status: 'active',
        joinedAt: new Date().toISOString(),
      });

      const userRef = doc(db, 'users', user.uid);
      await setDoc(
        userRef,
        stripUndefinedForFirestore({
          uid: user.uid,
          email: user.email,
          profileData: normalized,
          onboarded: true,
          profileComplete: false,
          displayName: normalized.fullName || user.displayName || userData?.displayName || '',
          phoneNumber: normalized.phone || '',
          currentWorkspaceId: workspaceRef.id,
          termsAcceptedAt: new Date().toISOString(),
          termsVersion: TERMS_VERSION,
          updatedAt: new Date().toISOString(),
        }),
        { merge: true },
      );

      const { accountService } = await import('../lib/account/accountService');
      await accountService.ensureAccountForUser(user.uid, {
        planTier: 'free',
        displayName: normalized.fullName,
        profileData: normalized,
      });
      await accountService.linkWorkspaceToAccount(user.uid, workspaceRef.id);

      await refreshUser();
      setShowTerms(false);
      setShowWelcomeMoment(true);
      setIsSubmitting(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
      showError('Failed to save profile data.');
      setIsSubmitting(false);
    }
  };

  const submitCompleteProfile = async (data: ProfileData = formData) => {
    if (!user) return;

    const deferred = validateDeferredProfile(data);
    if (!deferred.valid) {
      setStepErrors(deferred.errors);
      showError('Please fix the highlighted fields before submitting.');
      return;
    }

    const normalized = normalizeProfileData(data);
    const existing = (userData?.profileData as ProfileData | undefined) ?? {};
    const merged = normalizeProfileData({ ...existing, ...normalized });

    setIsSubmitting(true);

    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(
        userRef,
        stripUndefinedForFirestore({
          uid: user.uid,
          email: user.email,
          profileData: merged,
          profileComplete: true,
          displayName: merged.fullName || user.displayName || userData?.displayName || '',
          phoneNumber: merged.phone || userData?.phoneNumber || '',
          updatedAt: new Date().toISOString(),
        }),
        { merge: true },
      );

      await refreshUser();
      showSuccess('Profile complete!');
      navigate('/dashboard');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${user.uid}`);
      showError('Failed to save profile data.');
      setIsSubmitting(false);
    }
  };

  const renderQuestionBody = (q: OnboardingQuestion) => {
    if (q.type === 'role-select') {
      return (
        <>
          <RoleSelectCards
            value={formData.accountType}
            onSelect={handleAccountTypeSelect}
          />
          {stepErrors.accountType && (
            <p className="text-red-400 text-sm mt-4">{stepErrors.accountType}</p>
          )}
        </>
      );
    }

    if (q.type === 'phone') {
      return (
        <>
          <PhoneInput
            phone={formData.phone || ''}
            phoneCountry={formData.phoneCountry || DEFAULT_PHONE_COUNTRY}
            onChange={handlePhoneChange}
            error={stepErrors.phone}
            variant="onboarding"
          />
        </>
      );
    }

    if (q.type === 'contact-info') {
      return (
        <div className="space-y-6">
          <div>
            <label className={cn(onboardingClasses.microLabel, 'mb-2 block')}>
              Full Name
            </label>
            <input
              type="text"
              data-onboarding-focus
              className={cn(
                'w-full text-2xl md:text-3xl bg-transparent border-b-2 py-3 outline-none placeholder:text-white/15 font-medium transition-all duration-300',
                stepErrors.fullName
                  ? onboardingClasses.textInputError
                  : 'border-white/10 focus:border-[#C5CED9] focus:shadow-[0_1px_0_0_rgba(197,206,217,0.4)]',
              )}
              placeholder="Muhammad Ali Jinnah"
              value={formData.fullName || ''}
              onChange={(e) => handleTextChange('fullName', e.target.value)}
              onBlur={(e) => handleTextBlur('fullName', e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {stepErrors.fullName && (
              <p className="text-red-400 text-sm mt-2">{stepErrors.fullName}</p>
            )}
          </div>
          <div>
            <label className={cn(onboardingClasses.microLabel, 'mb-2 block mt-6')}>
              Phone Number
            </label>
            <PhoneInput
              phone={formData.phone || ''}
              phoneCountry={formData.phoneCountry || DEFAULT_PHONE_COUNTRY}
              onChange={handlePhoneChange}
              error={stepErrors.phone}
              variant="onboarding"
            />
          </div>
        </div>
      );
    }

    if (q.type === 'text') {
      const isOptional = q.required === false;
      const fieldKey = q.id as keyof ProfileData;
      const fieldValue = (formData[fieldKey] as string) || '';

      if (q.id === 'lawCollege') {
        return (
          <>
            <LawCollegeAutocompleteInput
              value={fieldValue}
              onChange={(v) => handleTextChange(fieldKey, v)}
              onBlur={(v) => handleTextBlur(fieldKey, v)}
              onKeyDown={handleKeyDown}
              error={stepErrors[q.id]}
              placeholder={q.placeholder}
            />
          </>
        );
      }

      if (q.id === 'city') {
        return (
          <>
            <CityAutocompleteInput
              value={fieldValue}
              onChange={(v) => handleTextChange(fieldKey, v)}
              onBlur={(v) => handleTextBlur(fieldKey, v)}
              onKeyDown={handleKeyDown}
              error={stepErrors[q.id]}
              placeholder={q.placeholder}
              province={formData.province}
              variant="onboarding"
            />
            {isOptional && !stepErrors[q.id] && (
              <p className="mt-3 text-sm text-[#8B95A8]">Optional — press Continue or Enter to skip</p>
            )}
          </>
        );
      }

      return (
        <>
          <input
            type="text"
            data-onboarding-focus
            className={cn(
              onboardingClasses.textInput,
              stepErrors[q.id] && onboardingClasses.textInputError,
            )}
            placeholder={q.placeholder}
            value={fieldValue}
            onChange={(e) => handleTextChange(fieldKey, e.target.value)}
            onBlur={(e) => handleTextBlur(fieldKey, e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {stepErrors[q.id] && (
            <p className="text-red-400 text-sm mt-2">{stepErrors[q.id]}</p>
          )}
          {isOptional && !stepErrors[q.id] && (
            <p className="mt-3 text-sm text-[#8B95A8]">Optional — press Continue or Enter to skip</p>
          )}
        </>
      );
    }

    if (q.type === 'single-select' || q.type === 'multi-select') {
      return (
        <>
          <div
            className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto custom-scrollbar pr-4 pb-12"
            data-onboarding-scroll-area
          >
            {q.options?.map((opt) => {
              const val = formData[q.id as keyof ProfileData];
              const isSelected =
                q.type === 'multi-select'
                  ? ((val as string[]) || []).includes(opt)
                  : val === opt;

              return (
                <div
                  key={opt}
                  onClick={() => handleSelectOption(q.id, opt, q.type === 'multi-select')}
                  className={cn(
                    onboardingClasses.selectRow,
                    isSelected
                      ? onboardingClasses.selectRowSelected
                      : onboardingClasses.selectRowDefault,
                  )}
                >
                  <div
                    className={cn(
                      'w-5 h-5 shrink-0 border flex items-center justify-center transition-all duration-300',
                      q.type === 'single-select' ? 'rounded-full' : 'rounded-md',
                      isSelected
                        ? 'border-[#C5CED9] bg-white/15'
                        : 'border-white/25 group-hover:border-white/40',
                    )}
                  >
                    {isSelected &&
                      (q.type === 'single-select' ? (
                        <div className="w-2 h-2 rounded-full bg-[#E8EDF4]" />
                      ) : (
                        <Check className="w-3 h-3 text-[#E8EDF4]" strokeWidth={2} />
                      ))}
                  </div>
                  <span className="text-lg md:text-xl font-medium text-[#F4F6F8]">{opt}</span>
                </div>
              );
            })}
          </div>
          {stepErrors[q.id] && <p className="text-red-400 text-sm mt-2">{stepErrors[q.id]}</p>}
        </>
      );
    }

    return null;
  };

  return (
    <OnboardingShell>
      <motion.div ref={onboardingRootRef} className="flex-1 relative z-10 flex flex-col min-w-0 min-h-0">
        <header className="relative z-20 shrink-0 flex flex-col items-center gap-5 px-6 pt-8 pb-4 md:pt-10 md:pb-5">
          <LexGatWordmark className="h-11 md:h-12 w-auto opacity-90 drop-shadow-[0_0_24px_rgba(255,255,255,0.12)]" />
          <motion.div className={cn(onboardingClasses.sidebarDivider, 'max-w-xl w-full')} aria-hidden />
          {!isSignup && (
            <p className="text-sm text-[#8B95A8] tracking-wide text-center -mt-2">
              Please complete your profile
            </p>
          )}
          <OnboardingStepWheel
            className="max-w-3xl"
            questions={questions}
            currentStep={currentStep}
            formData={formData}
            onGoToStep={goToStep}
          />
        </header>

        <div className="relative flex-1 min-h-0 overflow-hidden">
        <div className="absolute right-6 bottom-6 md:right-12 md:bottom-12 z-50 hidden md:flex flex-col gap-2">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className={onboardingClasses.navBtn}
            aria-label="Previous step"
          >
            <ChevronUp className="w-5 h-5 text-[#C5CED9]" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={handleNext}
            className={onboardingClasses.navBtn}
            aria-label="Next step"
          >
            <ChevronDown className="w-5 h-5 text-[#C5CED9]" strokeWidth={1.5} />
          </button>
        </div>

        <motion.div
          className="flex flex-col h-full w-full"
          animate={{ y: `-${currentStep * 100}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {questions.map((q, idx) => {
            const isActive = idx === currentStep;
            const hasValue = isStepCompleted(idx, formData, questions);
            const showOk =
              q.type === 'text' ||
              q.type === 'multi-select' ||
              q.type === 'contact-info' ||
              q.type === 'phone';

            return (
              <motion.div
                key={q.id}
                data-onboarding-active-step={isActive ? 'true' : undefined}
                className="h-full w-full flex items-center justify-center shrink-0 p-6 md:p-12 relative z-10"
              >
                <div
                  className={`w-full max-w-2xl transition-all duration-700 ${isActive ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`}
                >
                  <h1 className={onboardingClasses.heading}>{q.title}</h1>

                  {q.subtitle && <p className={onboardingClasses.subtitle}>{q.subtitle}</p>}

                  <div className="mt-8 md:mt-12">{renderQuestionBody(q)}</div>

                  <AnimatePresence>
                    {showOk && hasValue && isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="mt-8"
                      >
                        <button
                          onClick={handleNext}
                          disabled={isSubmitting}
                          className={onboardingClasses.primaryBtn}
                        >
                          {currentStep === questions.length - 1
                            ? isSubmitting
                              ? 'Saving...'
                              : isSignup
                                ? 'Continue'
                                : 'Complete Profile'
                            : q.required === false && q.type === 'text'
                              ? 'Skip'
                              : 'Continue'}
                          <Check className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                        <p className="mt-3 text-sm text-[#8B95A8]">
                          Press <span className="text-[#C5CED9] font-medium">Enter ↵</span>
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
        </div>
      </motion.div>

      <AnimatePresence>
        {isSignup && showTerms && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setShowTerms(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5, bounce: 0 }}
              className="bg-white text-slate-900 w-full max-w-3xl max-h-[85vh] rounded-2xl md:rounded-[2rem] shadow-2xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-5 md:px-8 md:py-6 border-b border-slate-100 bg-slate-50">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight">Terms & Conditions</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Please read and accept our terms to complete setup.
                  </p>
                </div>
                <button
                  onClick={() => setShowTerms(false)}
                  className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-full transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-8 space-y-6 text-sm text-slate-600 shadow-[inset_0_2px_10px_rgba(0,0,0,0.02)]">
                <TermsContent />
              </div>

              <div className="p-6 md:p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center gap-4 justify-between">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div
                    className={cn(
                      'w-6 h-6 rounded-md flex items-center justify-center border-2 transition-colors',
                      termsAccepted
                        ? 'bg-[#0A0A0B] border-[#0A0A0B] text-white'
                        : 'border-slate-300 text-transparent group-hover:border-slate-400',
                    )}
                  >
                    <Check className="w-4 h-4" />
                  </div>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <span className="text-sm font-medium text-slate-700 select-none">
                    I have read and accept the Terms & Conditions
                  </span>
                </label>

                <button
                  onClick={() => submitSignup()}
                  disabled={!termsAccepted || isSubmitting}
                  className={cn(
                    onboardingClasses.primaryBtn,
                    'w-full sm:w-auto disabled:opacity-50 disabled:pointer-events-none disabled:hover:scale-100',
                  )}
                >
                  {isSubmitting ? 'Saving...' : 'Accept & Complete'}
                  {termsAccepted && !isSubmitting && <Check className="w-5 h-5" strokeWidth={1.5} />}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showWelcomeMoment && (
          <OnboardingWelcomeMoment onComplete={() => navigate('/dashboard')} />
        )}
      </AnimatePresence>
    </OnboardingShell>
  );
}

